// src/pages/AdminParametres.jsx
import React, { useEffect, useState, useRef } from 'react';
import PageLayout from '../components/PageLayout';
import Button from '../components/Button';
import {
  listTypesSalles,
  createTypeSalle,
  updateTypeSalle,
  deleteTypeSalle
} from '../api/salles';

import {
  listExternalLinks,
  createExternalLink,
  updateExternalLink,
  deleteExternalLink,
} from '../api/externalLinks';
import toast from 'react-hot-toast';
import { CirclePlus, Trash2, Pencil, GripVertical } from 'lucide-react';
import { createPortal } from 'react-dom';
export default function AdminParametres(){

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typesSalles, setTypesSalles] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);

  const [createTypeSalleOpen, setCreateTypeSalleOpen] = useState(false);
  const [createTypeSalleForm, setCreateTypeSalleForm] = useState({});
  const [updateTypeSalleOpen, setUpdateTypeSalleOpen] = useState(false);
  const [updateTypeSalleForm, setUpdateTypeSalleForm] = useState({});
  const [deleteTypeSalleOpen, setDeleteTypeSalleOpen] = useState(false);
  const [deleteTypeSalleForm, setDeleteTypeSalleForm] = useState({});

  const [createExternalLinkOpen, setCreateExternalLinkOpen] = useState(false);
  const [createExternalLinkForm, setCreateExternalLinkForm] = useState({});
  const [updateExternalLinkOpen, setUpdateExternalLinkOpen] = useState(false);
  const [updateExternalLinkForm, setUpdateExternalLinkForm] = useState({});
  const [deleteExternalLinkOpen, setDeleteExternalLinkOpen] = useState(false);
  const [deleteExternalLinkForm, setDeleteExternalLinkForm] = useState({});

  const dragExternalLinkItem = useRef(null);
  const dragExternalLinkOverItem = useRef(null);

  const handleSortExternalLinks = async () => {
    let _links = [...externalLinks];
    const draggedItemContent = _links.splice(dragExternalLinkItem.current, 1)[0];
    _links.splice(dragExternalLinkOverItem.current, 0, draggedItemContent);

    dragExternalLinkItem.current = null;
    dragExternalLinkOverItem.current = null;

    setExternalLinks(_links);

    try {
      await Promise.all(
        _links.map((link, index) =>
          updateExternalLink(link.id, { ...link, ordre: index })
        )
      );
      toast.success('Ordre mis à jour');
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde de l'ordre");
    }
  };

  const handleSetUpdateTypeSalleForm = (typeSalle) => {
    setUpdateTypeSalleForm({
      libelle: typeSalle.libelle,
      id: typeSalle.id,
    });
  };

  const handleSetUpdateExternalLinkForm = (externalLink) => {
    setUpdateExternalLinkForm({
      libelle: externalLink.libelle,
      id: externalLink.id,
      url: externalLink.url
    });
  };

  const refresh = async () => {
    setError('');
    setLoading(true);
    try {
      const fts = await listTypesSalles();
      setTypesSalles(fts);

      const fel = await listExternalLinks();
      setExternalLinks(fel.sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);


  const onCreateTypeSalle = async (e) => {
    e.preventDefault();
    try {
      if (!createTypeSalleForm.libelle?.trim()) {
        throw new Error('Libelle requis');
      }

      const payload = { ...createTypeSalleForm };
      await createTypeSalle(payload);
      toast.success('Type créé');
      setCreateTypeSalleOpen(false);
      setCreateTypeSalleForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onUpdateTypeSalle = async (e) => {
    e.preventDefault();
    try {
      if (!updateTypeSalleForm.libelle?.trim()) {
        throw new Error('Libelle requis');
      }
      const payload = { ...updateTypeSalleForm };
      await updateTypeSalle(updateTypeSalleForm.id, payload);
      toast.success('Type modifié');
      setUpdateTypeSalleOpen(false);
      setUpdateTypeSalleForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDeleteTypeSalle = async (e) => {
    e.preventDefault();
    try {
      if (!deleteTypeSalleForm.id?.trim()) {
        throw new Error('id requis');
      }
      await deleteTypeSalle(deleteTypeSalleForm.id);
      toast.success('Type supprimé');
      setDeleteTypeSalleOpen(false);
      setDeleteTypeSalleForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onCreateExternalLink = async (e) => {
    e.preventDefault();
    try {
      if (!createExternalLinkForm.libelle?.trim()) {
        throw new Error('Libelle requis');
      }

      if (!createExternalLinkForm.url?.trim()) {
        throw new Error('URL requise');
      }

      const payload = { ...createExternalLinkForm };
      await createExternalLink(payload);
      toast.success('Lien externe créé');
      setCreateExternalLinkOpen(false);
      setCreateExternalLinkForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onUpdateExternalLink = async (e) => {
    e.preventDefault();
    try {
      if (!updateExternalLinkForm.libelle?.trim()) {
        throw new Error('Libelle requis');
      }

      if (!updateExternalLinkForm.url?.trim()) {
        throw new Error('URL requise');
      }
      const payload = { ...updateExternalLinkForm };
      await updateExternalLink(updateExternalLinkForm.id, payload);
      toast.success('Lien externe modifié');
      setUpdateExternalLinkOpen(false);
      setUpdateExternalLinkForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDeleteExternalLink = async (e) => {
    e.preventDefault();
    try {
      if (!deleteExternalLinkForm.id?.trim()) {
        throw new Error('id requis');
      }
      await deleteExternalLink(deleteExternalLinkForm.id);
      toast.success('Lien externe supprimé');
      setDeleteExternalLinkOpen(false);
      setDeleteExternalLinkForm({});
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <PageLayout title="Administration — Paramètres">
      {createTypeSalleOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setCreateTypeSalleOpen(false)}
            />
            <form
              onSubmit={onCreateTypeSalle}
              className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              <div className="md:col-span-2">
                <h2 className="text-lg font-semibold mb-1">Nouveau Type</h2>
              </div>
              <div className="md:col-span-2">
                <label>Libellé:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 md:col-span-2 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Libelle"
                  value={createTypeSalleForm.libelle}
                  onChange={(e) =>
                    setCreateTypeSalleForm((f) => ({ ...f, libelle: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={() => setCreateTypeSalleOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">Créer</Button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {updateTypeSalleOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setUpdateTypeSalleOpen(false)}
            />
            <form
              onSubmit={onUpdateTypeSalle}
              className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              <div className="md:col-span-2">
                <h2 className="md:col-span-2 text-lg font-semibold mb-1">
                  Modifier le type de salle
                </h2>
              </div>
              <div className="md:col-span-2">
                <label>Libellé:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Libelle"
                  value={updateTypeSalleForm.libelle}
                  onChange={(e) =>
                    setUpdateTypeSalleForm((f) => ({ ...f, libelle: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={() => setUpdateTypeSalleOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">Modifier</Button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {deleteTypeSalleOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setDeleteTypeSalleOpen(false)}
            />
            <div className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="md:col-span-2 space-y-2">
                <p>
                  Êtes-vous sûr de vouloir supprimer le type{' '}
                  <span className="bg-gray-400 pl-1 pr-1">{deleteTypeSalleForm.libelle}</span>?
                </p>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={onDeleteTypeSalle}>
                  Confirmer la suppression
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {createExternalLinkOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setCreateExternalLinkOpen(false)}
            />
            <form
              onSubmit={onCreateExternalLink}
              className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              <div className="md:col-span-2">
                <h2 className="text-lg font-semibold mb-1">Nouveau Lien Externe</h2>
              </div>
              <div className="md:col-span-1">
                <label>Libellé:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 md:col-span-2 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Libelle"
                  value={createExternalLinkForm.libelle}
                  onChange={(e) =>
                    setCreateExternalLinkForm((f) => ({ ...f, libelle: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-1">
                <label>URL:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 md:col-span-2 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="URL"
                  value={createExternalLinkForm.url}
                  onChange={(e) =>
                    setCreateExternalLinkForm((f) => ({ ...f, url: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={() => setCreateExternalLinkOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">Créer</Button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {updateExternalLinkOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setUpdateExternalLinkOpen(false)}
            />
            <form
              onSubmit={onUpdateExternalLink}
              className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              <div className="md:col-span-2">
                <h2 className="md:col-span-2 text-lg font-semibold mb-1">
                  Modifier le lien externe
                </h2>
              </div>
              <div className="md:col-span-1">
                <label>Libellé:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Libelle"
                  value={updateExternalLinkForm.libelle}
                  onChange={(e) =>
                    setUpdateExternalLinkForm((f) => ({ ...f, libelle: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-1">
                <label>URL:</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="URL"
                  value={updateExternalLinkForm.url}
                  onChange={(e) =>
                    setUpdateExternalLinkForm((f) => ({ ...f, url: e.target.value }))
                  }
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={() => setUpdateExternalLinkOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">Modifier</Button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {deleteExternalLinkOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/30">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setDeleteExternalLinkOpen(false)}
            />
            <div className="relative z-10  w-max[80vw]  md:max-w-3xl mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="md:col-span-2 space-y-2">
                <p>
                  Êtes-vous sûr de vouloir supprimer le lien{' '}
                  <span className="bg-gray-400 pl-1 pr-1">{deleteExternalLinkForm.libelle}</span>?
                </p>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" onClick={onDeleteExternalLink}>
                  Confirmer la suppression
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
          <div className="mt-3 bg-white rounded-xl shadow p-3 pb-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="px-4 pt-2 pb-2 items-center justify-between mb-3 flex md:col-span-4">
              <h1 className="text-xl font-semibold">Gestion des Types de salles</h1>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setCreateTypeSalleOpen(true);
                  }}
                >
                  <CirclePlus className="inline-block" /> Ajouter un type de salle
                </Button>
              </div>
            </div>
            {typesSalles.map((ts) => {
              return (
                <div
                  key={ts.id}
                  className="md:col-span-1 bg-gradient-to-r from-[#666666] to-[#999999] text-slate-50 rounded-xl shadow p-3 h-full grid grid-cols-5"
                >
                  <p className="col-span-4">{ts.libelle}</p>
                  <div className="col-span-1 flex justify-center gap-3">
                    <button
                      className="text-white"
                      onClick={() => {
                        handleSetUpdateTypeSalleForm(ts);
                        setUpdateTypeSalleOpen(true);
                      }}
                    >
                      <Pencil />
                    </button>
                    <button
                      className="text-white"
                      onClick={() => {
                        setDeleteTypeSalleForm({ id: ts.id, libelle: ts.libelle });
                        setDeleteTypeSalleOpen(true);
                      }}
                    >
                      <Trash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 bg-white rounded-xl shadow p-3 pb-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="px-4 pt-2 pb-2 items-center justify-between mb-3 flex md:col-span-2">
              <h1 className="text-xl font-semibold">Gestion des Liens externes</h1>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setCreateExternalLinkOpen(true);
                  }}
                >
                  <CirclePlus className="inline-block" /> Ajouter un lien externe
                </Button>
              </div>
            </div>
            {externalLinks.map((el, index) => (
              <div
                key={el.id}
                draggable
                onDragStart={() => (dragExternalLinkItem.current = index)}
                onDragEnter={() => (dragExternalLinkOverItem.current = index)}
                onDragEnd={handleSortExternalLinks}
                onDragOver={(e) => e.preventDefault()}
                className="md:col-span-1 bg-gradient-to-r from-[#f5ebb3] to-[#b8b082] text-black rounded-xl shadow p-3 h-full grid grid-cols-6 items-center cursor-move transition-transform active:scale-95 active:shadow-lg"
              >
                {/* Poignée de drag */}
                <div className="col-span-1 flex justify-center opacity-50">
                  <GripVertical />
                </div>

                <div className="col-span-4 min-w-0 pr-2">
                  <p className="font-medium truncate">{el.libelle}</p>
                  <p className="truncate text-sm">
                    <span className="font-semibold text-xs opacity-70">URL:</span>{' '}
                    <a
                      href={el.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-800 hover:underline"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      {el.url}
                    </a>
                  </p>
                </div>

                <div className="col-span-1 flex justify-center gap-3">
                  <button
                    className="text-slate-800 hover:text-black hover:scale-110 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetUpdateExternalLinkForm(el);
                      setUpdateExternalLinkOpen(true);
                    }}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    className="text-red-700 hover:text-red-900 hover:scale-110 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteExternalLinkForm({ id: el.id, libelle: el.libelle });
                      setDeleteExternalLinkOpen(true);
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </PageLayout>
  );
}


